import axios from "axios";

const envUrl = process.env.REACT_APP_BACKEND_URL;
const sameOrigin = typeof window !== "undefined" ? window.location.origin : "";
// The API is always served on the same origin behind /api, so ignore a build-time URL that
// points at a different host (e.g. a stale custom domain) which would break every request.
const hostOf = (u) => { try { return new URL(u).host; } catch { return ""; } };
const BACKEND_URL = !envUrl || (sameOrigin && hostOf(envUrl) !== hostOf(sameOrigin)) ? sameOrigin : envUrl;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API });

const TOKEN_KEY = "xyz_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };

http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && getToken()) {
      setToken(null);
      window.dispatchEvent(new Event("xyz-unauthorized"));
    }
    return Promise.reject(err);
  }
);

const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const authApi = {
  login: (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  users: () => http.get("/auth/users").then((r) => r.data),
  addUser: (payload) => http.post("/auth/users", payload).then((r) => r.data),
  removeUser: (id) => http.delete(`/auth/users/${id}`).then((r) => r.data),
  setPassword: (id, password) => http.patch(`/auth/users/${id}/password`, { password }).then((r) => r.data),
};

export async function streamPostForm(path, formData, onChunk) {
  const res = await fetch(`${API}${path}`, { method: "POST", body: formData, headers: { ...authHeaders() } });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = dec.decode(value, { stream: true });
    full += chunk;
    onChunk(chunk, full);
  }
  if (full.includes("[ERROR]")) throw new Error(full);
  return full;
}

export async function streamPost(path, payload, onChunk) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = dec.decode(value, { stream: true });
    full += chunk;
    onChunk(chunk, full);
  }
  if (full.includes("[ERROR]")) throw new Error(full);
  return full;
}

export const dealsApi = {
  list: () => http.get("/deals").then((r) => r.data),
  create: (payload) => http.post("/deals", payload).then((r) => r.data),
  update: (id, payload) => http.put(`/deals/${id}`, payload).then((r) => r.data),
  remove: (id) => http.delete(`/deals/${id}`).then((r) => r.data),
};

export const platformsApi = {
  list: () => http.get("/platforms").then((r) => r.data),
  create: (p) => http.post("/platforms", p).then((r) => r.data),
  update: (id, p) => http.put(`/platforms/${id}`, p).then((r) => r.data),
  remove: (id) => http.delete(`/platforms/${id}`).then((r) => r.data),
  thesis: (p) => http.post("/platforms/thesis", p).then((r) => r.data),
};

export const podcastApi = {
  status: () => http.get("/podcast/status").then((r) => r.data),
  setConfig: (payload) => http.put("/podcast/config", payload).then((r) => r.data),
  scrape: (params = {}) => http.post("/podcast/scrape", null, { params }).then((r) => r.data),
  job: (id) => http.get(`/podcast/jobs/${id}`).then((r) => r.data),
  intros: (payload) => http.post("/podcast/intros", payload).then((r) => r.data),
  chaseEmails: (limit = 20) => http.post("/podcast/chase-emails", null, { params: { limit } }).then((r) => r.data),
};

export const investorsApi = {
  list: (params = {}) => http.get("/investors", { params }).then((r) => r.data),
  stats: () => http.get("/investors/stats").then((r) => r.data),
  create: (payload) => http.post("/investors", payload).then((r) => r.data),
  update: (id, payload) => http.put(`/investors/${id}`, payload).then((r) => r.data),
  patch: (id, patch) => http.patch(`/investors/${id}`, patch).then((r) => r.data),
  remove: (id) => http.delete(`/investors/${id}`).then((r) => r.data),
  bulkDelete: (ids) => http.post("/investors/bulk-delete", { ids }).then((r) => r.data),
  setStatus: (id, status) => http.put(`/investors/${id}/status`, { status }).then((r) => r.data),
  enrich: (id) => http.post(`/investors/${id}/enrich`).then((r) => r.data),
  bulkEnrich: (type, limit = 12) => http.post(`/investors/bulk-enrich`, null, { params: { type, limit } }).then((r) => r.data),
  dedupe: () => http.post("/investors/dedupe").then((r) => r.data),
  fixNames: () => http.post("/investors/fix-names").then((r) => r.data),
  matchDeal: (payload) => http.post("/investors/match-deal", payload).then((r) => r.data),
  uploadCsv: (formData) =>
    http.post("/investors/upload-csv", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  sendTeaser: (payload) => http.post("/investors/send-teaser", payload).then((r) => r.data),
  sendLog: () => http.get("/investors/send-log").then((r) => r.data),
};

export const researchApi = {
  entities: (payload) => http.post("/research/entities", payload).then((r) => r.data),
  apollo: (payload) => http.post("/enrich/apollo", payload).then((r) => r.data),
};

export const lendersApi = {
  list: (params) => http.get("/lenders", { params }).then((r) => r.data),
  stats: () => http.get("/lenders/stats").then((r) => r.data),
  create: (p) => http.post("/lenders", p).then((r) => r.data),
  bulk: (items) => http.post("/lenders/bulk", items).then((r) => r.data),
  update: (id, patch) => http.patch(`/lenders/${id}`, patch).then((r) => r.data),
  remove: (id) => http.delete(`/lenders/${id}`).then((r) => r.data),
};

export const placementApi = {
  list: (params) => http.get("/placement-agents", { params }).then((r) => r.data),
  stats: () => http.get("/placement-agents/stats").then((r) => r.data),
  create: (p) => http.post("/placement-agents", p).then((r) => r.data),
  bulk: (items) => http.post("/placement-agents/bulk", items).then((r) => r.data),
  update: (id, patch) => http.patch(`/placement-agents/${id}`, patch).then((r) => r.data),
  remove: (id) => http.delete(`/placement-agents/${id}`).then((r) => r.data),
};

export const contactApi = {
  find: (payload) => http.post("/ai/find-contact", payload).then((r) => r.data),
};

export const chatApi = {
  web: (payload) => http.post("/ai/chat-web", payload).then((r) => r.data),
};

export const exportApi = {
  templateModel: (payload) => http.post("/export/template-model", payload).then((r) => r.data),
};
export const shareApi = {
  thesisDraft: (payload) => http.post("/share/thesis-draft", payload).then((r) => r.data),
  create: (payload) => http.post("/share", payload).then((r) => r.data),
  list: () => http.get("/share").then((r) => r.data),
  byDeal: (key, alts = []) => http.get(`/share/by-deal/${encodeURIComponent(key)}`, { params: { alt: alts.filter(Boolean).join(",") } }).then((r) => r.data),
  remove: (id) => http.delete(`/share/${id}`).then((r) => r.data),
  activity: (id) => http.get(`/share/${id}/activity`).then((r) => r.data),
  deleteCommit: (id, cid) => http.delete(`/share/${id}/commits/${cid}`).then((r) => r.data),
  invites: (id, recipients) => http.post(`/share/${id}/invites`, { recipients }).then((r) => r.data),
  inviteList: (id) => http.get(`/share/${id}/invites`).then((r) => r.data),
  revokeInvite: (id, iid) => http.delete(`/share/${id}/invites/${iid}`).then((r) => r.data),
  questionsAll: (id) => http.get(`/share/${id}/questions/all`).then((r) => r.data),
  answer: (id, qid, payload) => http.put(`/share/${id}/questions/${qid}`, payload).then((r) => r.data),
  draftAnswer: (id, qid) => http.post(`/share/${id}/questions/${qid}/draft`).then((r) => r.data),
  deleteQuestion: (id, qid) => http.delete(`/share/${id}/questions/${qid}`).then((r) => r.data),
  questions: (id, session) => axios.get(`${API}/share/${id}/questions`, { params: { session } }).then((r) => r.data),
  accept: (id, sessionId) => axios.post(`${API}/share/${id}/accept`, { sessionId }).then((r) => r.data),
  ask: (id, sessionId, question) => axios.post(`${API}/share/${id}/questions`, { sessionId, question }).then((r) => r.data),
  uploadDoc: (id, file, category, label) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category); fd.append("label", label || "");
    return http.post(`/share/${id}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteDoc: (id, did) => http.delete(`/share/${id}/docs/${did}`).then((r) => r.data),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/room-image", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  docUrl: (id, did, session) => `${API}/share/${id}/docs/${did}?session=${encodeURIComponent(session || "")}`,
  // Public room entry: the shared passcode (with self-stated name/email), a personal invite key,
  // or — for signed-in firm users — a staff preview.
  view: (id, { passcode = "", key = "", name = "", email = "", sessionId = "" } = {}) =>
    axios.post(`${API}/share/${id}/view`, { passcode, key, name, email, sessionId },
               { headers: passcode || key ? {} : authHeaders() }).then((r) => r.data),
  commit: (id, payload) => axios.post(`${API}/share/${id}/commit`, payload).then((r) => r.data),
  pass: (id, payload) => axios.post(`${API}/share/${id}/pass`, payload).then((r) => r.data),
  track: (id, sessionId, tab, seconds) => {
    const body = JSON.stringify({ sessionId, tab, seconds });
    return fetch(`${API}/share/${id}/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  },
};
export const nnnApi = {
  list: () => http.get("/nnn/buyers").then((r) => r.data),  create: (payload) => http.post("/nnn/buyers", payload).then((r) => r.data),
  bulk: (items) => http.post("/nnn/buyers/bulk", items).then((r) => r.data),
  remove: (id) => http.delete(`/nnn/buyers/${id}`).then((r) => r.data),
  find: (payload) => http.post("/nnn/find", payload).then((r) => r.data),
};


export const aiApi = {
  chat: (payload) => http.post("/ai/chat", payload).then((r) => r.data),
  teaser: (payload) => http.post("/ai/teaser", payload).then((r) => r.data),
  loi: (payload) => http.post("/ai/loi", payload).then((r) => r.data),
  market: (payload) => http.post("/ai/market", payload).then((r) => r.data),
  tam: (payload) => http.post("/ai/tam", payload).then((r) => r.data),
  comps: (payload) => http.post("/ai/comps", payload).then((r) => r.data),
  consolidators: (payload) => http.post("/ai/consolidators", payload).then((r) => r.data),
};

export const brokerApi = {
  send: (payload) => http.post("/broker/send", payload).then((r) => r.data),
};

export const emailApi = {
  sendDirect: (payload) => http.post("/email/send-direct", payload).then((r) => r.data),
  compose: (payload) => http.post("/email/compose", payload).then((r) => r.data),
};

export const scoutApi = {
  list: () => http.get("/scout/deals").then((r) => r.data),
  create: (payload) => http.post("/scout/deals", payload).then((r) => r.data),
  update: (id, payload) => http.put(`/scout/deals/${id}`, payload).then((r) => r.data),
  remove: (id) => http.delete(`/scout/deals/${id}`).then((r) => r.data),
  listSearches: () => http.get("/scout/searches").then((r) => r.data),
  createSearch: (payload) => http.post("/scout/searches", payload).then((r) => r.data),
  removeSearch: (id) => http.delete(`/scout/searches/${id}`).then((r) => r.data),
  parseUrl: (url) => http.post("/scout/parse-url", { url }).then((r) => r.data),
  researchBroker: (payload) => http.post("/scout/research-broker", payload).then((r) => r.data),
};

export const instantlyApi = {
  campaigns: () => http.get("/instantly/campaigns").then((r) => r.data),
  createCampaign: (payload) => http.post("/instantly/campaigns", typeof payload === "string" ? { name: payload } : payload).then((r) => r.data),
  addLeads: (payload) => http.post("/instantly/add-leads", payload).then((r) => r.data),
  inbox: (params = {}) => http.get("/instantly/inbox", { params }).then((r) => r.data),
  markRead: (threadId, emailId) => http.post(`/instantly/threads/${threadId}/read`, null, { params: { email_id: emailId } }).then((r) => r.data),
  reply: (payload) => http.post("/instantly/reply", payload).then((r) => r.data),
  classify: (payload) => http.post("/instantly/classify", payload).then((r) => r.data),
  setStatus: (payload) => http.post("/instantly/status", payload).then((r) => r.data),
  saveLead: (payload) => http.post("/instantly/save-lead", payload).then((r) => r.data),
  accounts: () => http.get("/instantly/accounts").then((r) => r.data),
  advance: (payload) => http.post("/instantly/advance", payload).then((r) => r.data),
  templates: (facts) => http.post("/instantly/templates", { facts }).then((r) => r.data),
  rewriteTemplate: (payload) => http.post("/instantly/templates/rewrite", payload).then((r) => r.data),
  webhookStatus: () => http.get("/instantly/webhook/status").then((r) => r.data),
  webhookConnect: (origin) => http.post("/instantly/webhook/connect", { origin }).then((r) => r.data),
  webhookDisconnect: () => http.delete("/instantly/webhook").then((r) => r.data),
  // EventSource cannot send an Authorization header, so it uses a 60-second single-purpose ticket.
  eventsUrl: async () => {
    const { ticket } = await http.post("/instantly/events/ticket").then((r) => r.data);
    return `${API}/instantly/events?ticket=${encodeURIComponent(ticket)}`;
  },
};

export const brokersApi = {
  list: (params) => http.get("/brokers", { params }).then((r) => r.data),
  stats: () => http.get("/brokers/stats").then((r) => r.data),
  create: (payload) => http.post("/brokers", payload).then((r) => r.data),
  bulk: (rows) => http.post("/brokers/bulk", { rows }).then((r) => r.data),
  update: (id, patch) => http.patch(`/brokers/${id}`, { patch }).then((r) => r.data),
  remove: (id) => http.delete(`/brokers/${id}`).then((r) => r.data),
  apollo: (payload) => http.post("/brokers/apollo", payload).then((r) => r.data),
  discover: (payload) => http.post("/brokers/discover", payload).then((r) => r.data),
  buyerEmail: (payload) => http.post("/brokers/buyer-email", payload).then((r) => r.data),
  enrich: (id) => http.post(`/brokers/${id}/enrich`).then((r) => r.data),
};

export const ownersApi = {
  list: (params) => http.get("/owners", { params }).then((r) => r.data),
  types: () => http.get("/owners/types").then((r) => r.data),
  create: (payload) => http.post("/owners", payload).then((r) => r.data),
  bulk: (rows) => http.post("/owners/bulk", { rows }).then((r) => r.data),
  update: (id, patch) => http.patch(`/owners/${id}`, { patch }).then((r) => r.data),
  remove: (id) => http.delete(`/owners/${id}`).then((r) => r.data),
};

export const offmarketApi = {
  criteria: (dealKey) => http.get("/offmarket/criteria", { params: { dealKey } }).then((r) => r.data),
  saveCriteria: (payload) => http.put("/offmarket/criteria", payload).then((r) => r.data),
  list: (params) => http.get("/offmarket/targets", { params }).then((r) => r.data),
  types: (dealKey) => http.get("/offmarket/types", { params: { dealKey } }).then((r) => r.data),
  stats: (dealKey) => http.get("/offmarket/stats", { params: { dealKey } }).then((r) => r.data),
  create: (payload) => http.post("/offmarket/targets", payload).then((r) => r.data),
  bulk: (payload) => http.post("/offmarket/targets/bulk", payload).then((r) => r.data),
  update: (id, patch) => http.patch(`/offmarket/targets/${id}`, { patch }).then((r) => r.data),
  remove: (id) => http.delete(`/offmarket/targets/${id}`).then((r) => r.data),
  activity: (id, patch) => http.post(`/offmarket/targets/${id}/activity`, { patch }).then((r) => r.data),
  score: (payload) => http.post("/offmarket/score", payload).then((r) => r.data),
  discover: (payload) => http.post("/offmarket/discover", payload).then((r) => r.data),
  apolloSearch: (payload) => http.post("/offmarket/apollo/search", payload).then((r) => r.data),
  enrich: (id) => http.post(`/offmarket/targets/${id}/enrich`).then((r) => r.data),
  enrichMany: (ids) => http.post("/offmarket/enrich-many", { ids }).then((r) => r.data),
  jobScore: (payload) => http.post("/offmarket/jobs/score", payload).then((r) => r.data),
  job: (id) => http.get(`/offmarket/jobs/${id}`).then((r) => r.data),
  apifyStatus: () => http.get("/offmarket/apify/status").then((r) => r.data),
  apifyStart: (payload) => http.post("/offmarket/apify/start", payload).then((r) => r.data),
  apifyPoll: (runId) => http.get(`/offmarket/apify/runs/${runId}`).then((r) => r.data),
};

export const bdApi = {
  platforms: () => http.get("/bd/platforms").then((r) => r.data),
  addPlatform: (payload) => http.post("/bd/platforms", payload).then((r) => r.data),
  removePlatform: (id) => http.delete(`/bd/platforms/${id}`).then((r) => r.data),
  saveIcp: (payload) => http.put("/bd/icp", payload).then((r) => r.data),
  list: (params) => http.get("/bd/prospects", { params }).then((r) => r.data),
  stats: (platformId) => http.get("/bd/stats", { params: { platformId } }).then((r) => r.data),
  bulk: (payload) => http.post("/bd/prospects/bulk", payload).then((r) => r.data),
  update: (id, patch) => http.patch(`/bd/prospects/${id}`, { patch }).then((r) => r.data),
  remove: (id) => http.delete(`/bd/prospects/${id}`).then((r) => r.data),
  activity: (id, patch) => http.post(`/bd/prospects/${id}/activity`, { patch }).then((r) => r.data),
  jobScore: (payload) => http.post("/bd/jobs/score", payload).then((r) => r.data),
  jobEnrich: (payload) => http.post("/bd/jobs/enrich", payload).then((r) => r.data),
  followups: (platformId) => http.get("/bd/followups", { params: { platformId } }).then((r) => r.data),
  nextStep: (id) => http.post(`/bd/prospects/${id}/next-step`).then((r) => r.data),
};

export const mailApi = {
  status: () => http.get("/mail/ms/status").then((r) => r.data),
  connect: (origin) => http.get("/mail/ms/connect", { params: { origin } }).then((r) => r.data),
  disconnect: () => http.post("/mail/ms/disconnect").then((r) => r.data),
  messages: (params = {}) => http.get("/mail/ms/messages", { params }).then((r) => r.data),
  thread: (id) => http.get(`/mail/ms/thread/${encodeURIComponent(id)}`).then((r) => r.data),
  markRead: (id) => http.post(`/mail/ms/messages/${encodeURIComponent(id)}/read`).then((r) => r.data),
  reply: (payload) => http.post("/mail/ms/reply", payload).then((r) => r.data),
  send: (payload) => http.post("/mail/ms/send", payload).then((r) => r.data),
  meeting: (payload) => http.post("/mail/ms/meeting", payload).then((r) => r.data),
};

export const firmApi = {  get: () => http.get("/firm").then((r) => r.data),
  update: (payload) => http.put("/firm", payload).then((r) => r.data),
};

export const intelApi = {
  match: (payload) => http.post("/intel/match", payload).then((r) => r.data),
  run: (id) => http.get(`/intel/run/${id}`).then((r) => r.data),
  last: (params = {}) => http.get("/intel/last", { params }).then((r) => r.data),
  research: (payload) => http.post("/intel/research", payload, { timeout: 180000 }).then((r) => r.data),
  apolloContacts: (payload) => http.post("/intel/apollo-contacts", payload, { timeout: 120000 }).then((r) => r.data),
  promote: (payload) => http.post("/intel/promote", payload).then((r) => r.data),
  toPipeline: (payload) => http.post("/intel/to-pipeline", payload).then((r) => r.data),
  searches: (params = {}) => http.get("/intel/searches", { params }).then((r) => r.data),
  saveSearch: (payload) => http.post("/intel/searches", payload).then((r) => r.data),
  deleteSearch: (id) => http.delete(`/intel/searches/${id}`).then((r) => r.data),
};

export const signalsApi = {
  scan: (payload) => http.post("/signals/scan", payload).then((r) => r.data),
  run: (id) => http.get(`/signals/run/${id}`).then((r) => r.data),
  last: (params = {}) => http.get("/signals/last", { params }).then((r) => r.data),
  searches: (params = {}) => http.get("/signals/searches", { params }).then((r) => r.data),
  saveSearch: (payload) => http.post("/signals/searches", payload).then((r) => r.data),
  deleteSearch: (id) => http.delete(`/signals/searches/${id}`).then((r) => r.data),
  digest: () => http.get("/signals/digest").then((r) => r.data),
  saveDigest: (payload) => http.put("/signals/digest", payload).then((r) => r.data),
  sendDigest: () => http.post("/signals/digest/send", {}, { timeout: 600000 }).then((r) => r.data),
};

export const foApi = {
  scan: (payload) => http.post("/family-offices/scan", payload).then((r) => r.data),
  run: (id) => http.get(`/family-offices/run/${id}`).then((r) => r.data),
  last: (params = {}) => http.get("/family-offices/last", { params }).then((r) => r.data),
};

export const thesisApi = {
  scan: (payload) => http.post("/theses/scan", payload).then((r) => r.data),
  run: (id) => http.get(`/theses/run/${id}`).then((r) => r.data),
  last: (params = {}) => http.get("/theses/last", { params }).then((r) => r.data),
  searches: (params = {}) => http.get("/theses/searches", { params }).then((r) => r.data),
  saveSearch: (payload) => http.post("/theses/searches", payload).then((r) => r.data),
  deleteSearch: (id) => http.delete(`/theses/searches/${id}`).then((r) => r.data),
};

export const folkApi = {
  status: () => http.get("/folk/status").then((r) => r.data),
  groups: () => http.get("/folk/groups").then((r) => r.data),
  sync: (payload) => http.post("/folk/sync", payload).then((r) => r.data),
  synced: (source) => http.get("/folk/synced", { params: source ? { source } : {} }).then((r) => r.data),
};

export const docsApi = {  list: (params = {}) => http.get("/docs", { params }).then((r) => r.data),
  upload: (file, meta = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v ?? ""));
    return http.post("/docs", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  saveBase64: (payload) => http.post("/docs/base64", payload).then((r) => r.data),
  patch: (id, patch) => http.patch(`/docs/${id}`, patch).then((r) => r.data),
  remove: (id) => http.delete(`/docs/${id}`).then((r) => r.data),
  base64: (id) => http.get(`/docs/${id}/base64`).then((r) => r.data),
  // Fetched with the auth header and saved from memory — the token never touches a URL.
  download: async (id, filename) => {
    const res = await http.get(`/docs/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "document";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  },
};

export const capitalApi = {
  list: (params = {}) => http.get("/capital/leads", { params }).then((r) => r.data),
  create: (payload) => http.post("/capital/leads", payload).then((r) => r.data),
  patch: (id, patch) => http.patch(`/capital/leads/${id}`, patch).then((r) => r.data),
  remove: (id) => http.delete(`/capital/leads/${id}`).then((r) => r.data),
  activity: (id, payload) => http.post(`/capital/leads/${id}/activity`, payload).then((r) => r.data),
  summary: (params = {}) => http.get("/capital/summary", { params }).then((r) => r.data),
  importInvestors: (payload) => http.post("/capital/import-investors", payload).then((r) => r.data),
  syncRoom: (payload) => http.post("/capital/sync-room", payload).then((r) => r.data),
  draftEmail: (id) => http.post(`/capital/leads/${id}/draft-email`).then((r) => r.data),
  sendEmail: (id, payload) => http.post(`/capital/leads/${id}/email`, payload).then((r) => r.data),
  digest: (payload) => http.post("/capital/digest", payload).then((r) => r.data),
  bulkDelete: (payload) => http.post("/capital/leads/bulk-delete", payload).then((r) => r.data),
  bulkPatch: (ids, patch) => http.post("/capital/leads/bulk-patch", { ids, patch }).then((r) => r.data),
  outreachContacts: (payload) => http.post("/capital/outreach-contacts", payload).then((r) => r.data),
  timeline: (params) => http.get("/capital/timeline", { params }).then((r) => r.data),
  saveTimeline: (payload) => http.put("/capital/timeline", payload).then((r) => r.data),
  generateTimeline: (payload) => http.post("/capital/timeline/generate", payload).then((r) => r.data),
  deleteTimeline: (params) => http.delete("/capital/timeline", { params }).then((r) => r.data),
};

export const teamApi = {
  assignments: (params) => http.get("/team/assignments", { params }).then((r) => r.data),
  assign: (payload) => http.post("/team/assign", payload).then((r) => r.data),
  unassign: (id) => http.delete(`/team/assignments/${id}`).then((r) => r.data),
  invite: (payload) => http.post("/team/invite", payload).then((r) => r.data),
  patchAssignment: (id, patch) => http.patch(`/team/assignments/${id}`, patch).then((r) => r.data),
  activity: (params) => http.get("/team/activity", { params }).then((r) => r.data),
};

export const debtApi = {
  list: (params) => http.get("/debt/leads", { params }).then((r) => r.data),
  create: (p) => http.post("/debt/leads", p).then((r) => r.data),
  patch: (id, p) => http.patch(`/debt/leads/${id}`, p).then((r) => r.data),
  remove: (id) => http.delete(`/debt/leads/${id}`).then((r) => r.data),
  activity: (id, p) => http.post(`/debt/leads/${id}/activity`, p).then((r) => r.data),
  sendEmail: (id, p) => http.post(`/debt/leads/${id}/email`, p).then((r) => r.data),
  importLenders: (p) => http.post("/debt/import-lenders", p).then((r) => r.data),
  upload: (id, file) => { const fd = new FormData(); fd.append("file", file); return http.post(`/debt/leads/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  openFile: async (fid) => { const r = await http.get(`/debt/attachments/${fid}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); },
  removeFile: (fid) => http.delete(`/debt/attachments/${fid}`).then((r) => r.data),
};

export const parseApi = {
  extractText: (formData) =>
    http.post("/parse/extract-text", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
};
